import { getFeature } from 'Cloud/Application/FeaturesEs6';
import {
    ANONYM_USER,
    IS_BIZ_USER,
    IS_CORP_USER,
    IS_IOS_MOBILE,
    IS_MOBILE_BROWSER,
    IS_REGULAR_USER,
    IS_WEBVIEW,
} from 'reactApp/appHelpers/configHelpers';
import { registerExperiment } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import type { Product } from 'reactApp/types/Billing';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';

interface BusinessTariffs {
    month: string[];
    year: string[];
}

const featurePrefix = 'ab-business-tariffs-promo-quota';

const businessTariffsPromoQuotaFeatureIos: 'a' | 'b' | false = IS_WEBVIEW && getFeature(`${featurePrefix}-ios`);

const businessTariffsPromoQuotaFeatureAndroid: 'a' | 'b' | false = IS_WEBVIEW && getFeature(`${featurePrefix}-android`);

const businessTariffsPromoQuotaFeatureWebview: 'a' | 'b' | false = IS_IOS_MOBILE
    ? businessTariffsPromoQuotaFeatureIos
    : businessTariffsPromoQuotaFeatureAndroid;

const businessTariffsPromoQuotaFeatureTouch: 'a' | 'b' | false = IS_MOBILE_BROWSER && getFeature(`${featurePrefix}-touch`);

const businessTariffsPromoQuotaFeatureMobile: 'a' | 'b' | false = IS_WEBVIEW
    ? businessTariffsPromoQuotaFeatureWebview
    : businessTariffsPromoQuotaFeatureTouch;

const defaultTariffs: BusinessTariffs = {
    month: [
        'W1TB_1m_business_discount',
        'W256GB_1m_business_discount',
        'W512GB_1m_business_discount',
        'W2TB_1m_business_discount',
        'W4TB_1m_business_discount',
    ],
    year: [
        'W1TB_1y_business_discount',
        'W256GB_1y_business_discount',
        'W512GB_1y_business_discount',
        'W2TB_1y_business_discount',
        'W4TB_1y_business_discount',
    ],
};
const businessTariffsPromoQuotaIos: BusinessTariffs = (IS_WEBVIEW && getFeature(`${featurePrefix}-tariffs-ios`)) || defaultTariffs;
const businessTariffsPromoQuotaAndroid: BusinessTariffs = (IS_WEBVIEW && getFeature(`${featurePrefix}-tariffs-android`)) || defaultTariffs;
const businessTariffsPromoQuotaWebview = IS_IOS_MOBILE ? businessTariffsPromoQuotaIos : businessTariffsPromoQuotaAndroid;
const businessTariffsPromoQuotaTouch = getFeature(`${featurePrefix}-tariffs-touch`) || defaultTariffs;
const resolvedTariffs = IS_WEBVIEW ? businessTariffsPromoQuotaWebview : businessTariffsPromoQuotaTouch;

const businessTariffsPromoQuotaAnonymousTouch = getFeature(`${featurePrefix}-anonymous-tariffs-touch`) || defaultTariffs;

const queryParams = getQueryParams();
const forcedByQuery = queryParams?.initTab === ETabsName.for_work;

export const businessTariffsPromoQuotaMobile = {
    enabled:
        (forcedByQuery || businessTariffsPromoQuotaFeatureMobile === 'b') &&
        (IS_WEBVIEW || IS_MOBILE_BROWSER) &&
        !IS_BIZ_USER &&
        (IS_REGULAR_USER || ANONYM_USER) &&
        !IS_CORP_USER,
    tariffs: resolvedTariffs,
    anonymousTariffs: businessTariffsPromoQuotaAnonymousTouch,
} as const;
const isBusinessProduct = (product: Product) => {
    return resolvedTariffs?.month?.includes(product.id) || resolvedTariffs?.year?.includes(product.id);
};

registerExperiment(
    'for-work-ms-mob',
    forcedByQuery ? 'q' : businessTariffsPromoQuotaFeatureMobile,
    [
        AnalyticEventNames.TARIFFS_SHOWN,
        AnalyticEventNames.TARIFF_BUY,
        AnalyticEventNames.TARIFF_CLICK,
        AnalyticEventNames.FAST_CHECKOUT_SHOWN,
    ],
    {
        mapEventNames: {
            TARIFFS_SHOWN: 'tariffs',
            TARIFF_BUY: 'buy',
            TARIFF_CLICK: 'click',
            FAST_CHECKOUT_SHOWN: 'checkout',
        },
        sendXrayArgs: {
            TARIFFS_SHOWN: ({ tariffs_line }) => ({ cancel: tariffs_line !== 'for_work' }),
            TARIFF_BUY: ({ product }) => ({ cancel: !isBusinessProduct(product) }),
            TARIFF_CLICK: ({ product }) => ({ cancel: !isBusinessProduct(product) }),
            FAST_CHECKOUT_SHOWN: ({ productId }) => ({ cancel: !isBusinessProduct({ id: productId } as Product) }),
        },
    }
);
